import { ColorVariantBadge } from './ColorVariantBadge';
import React from "react";
import { Plus, Trash2, X, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SIMULATION_SIZES } from "@/lib/constants";
import { useColorSettings } from "@/contexts/ColorSettingsContext";

function cellKey(color, size) {
  return `${color}::${size}`;
}

function RowQuickFill({ onApply }) {
  const [value, setValue] = React.useState("");
  const apply = () => {
    if (value === "") return;
    onApply?.(value);
  };
  return (
    <div className="flex items-center justify-center gap-1">
      <Input
        type="number"
        min="0"
        step="1"
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); apply(); } }}
        placeholder="0"
        className="h-8 w-12 px-1 text-center"
      />
      <Button type="button" variant="outline" size="icon" className="h-8 w-8 flex-shrink-0" onClick={apply} disabled={value === ""} title="Rellenar todas las tallas con este valor">
        <Wand2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

export function SimulationSizeColorMatrix({
  colors = [],
  quantities = {},
  contrasts = {},
  variants = {},
  hideContrasts = false,
  allowVariants = false,
  onChange,
  onContrastChange,
  onFillRow,
  onAddColor,
  onRemoveColor,
  readOnly = false,
  allowAddColors = true,
  colorsOnly = false,
}) {
  const { colorSettings } = useColorSettings();
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [colorSearchTerm, setColorSearchTerm] = React.useState("");
  const totalQuantity = colors.reduce(
    (sum, color) => sum + SIMULATION_SIZES.reduce((sizeSum, size) => sizeSum + Number(quantities[cellKey(color, size)] || 0), 0),
    0
  );

  const search = colorSearchTerm.trim().toLowerCase();
  const availableColors = React.useMemo(
    () =>
      Object.entries(colorSettings || {})
        .filter(([colorId, colorData]) => colorData?.active !== false && (allowVariants || !colors.includes(colorId)))
        .filter(([colorId, colorData]) =>
          search === "" ||
          colorId.toLowerCase().includes(search) ||
          (colorData?.description || "").toLowerCase().includes(search)
        )
        .sort((left, right) => (left[1]?.description || left[0]).localeCompare(right[1]?.description || right[0])),
    [colorSettings, colors, search, allowVariants]
  );

  const hasActiveColors = Object.values(colorSettings || {}).some((colorData) => colorData?.active !== false);

  return (
    <div className="space-y-3">
      {!readOnly && allowAddColors && (
        hasActiveColors ? (
          !pickerOpen ? (
            <Button type="button" variant="outline" size="sm" onClick={() => setPickerOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />{allowVariants ? "Añadir color / variante" : "Añadir color"}
            </Button>
          ) : (
          <div className="space-y-2 rounded border bg-gray-50 p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium text-gray-700">Añade un color disponible</span>
              <div className="flex items-center gap-2">
                <Input
                  value={colorSearchTerm}
                  onChange={(event) => setColorSearchTerm(event.target.value)}
                  placeholder="Buscar color..."
                  autoFocus
                  className="h-8 w-48"
                />
                <Button type="button" variant="ghost" size="sm" onClick={() => { setPickerOpen(false); setColorSearchTerm(""); }} title="Cerrar">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            {availableColors.length === 0 ? (
              <div className="py-2 text-center text-sm text-gray-500">
                {search ? `No se encontraron colores que coincidan con "${colorSearchTerm}"` : "Ya has añadido todos los colores disponibles."}
              </div>
            ) : (
              <div className="grid grid-cols-[repeat(auto-fill,minmax(96px,1fr))] gap-2 p-1">
                {availableColors.map(([colorId, colorData]) => (
                  <button
                    key={colorId}
                    type="button"
                    onClick={() => onAddColor?.(colorId)}
                    title={`${colorId}: ${colorData?.description || colorId}`}
                    className="flex flex-col items-center gap-1 rounded-lg border bg-white p-2 ring-1 ring-gray-200 transition-all duration-200 hover:scale-105 hover:shadow-sm hover:ring-2 hover:ring-blue-400"
                  >
                    <span
                      className="h-7 w-7 rounded-full border border-gray-300"
                      style={{ backgroundColor: colorData?.hex || "#d1d5db" }}
                    />
                    <span className="line-clamp-1 w-full text-center text-xs text-gray-700">{colorData?.description || colorId}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          )
        ) : (
          <div className="rounded border border-dashed bg-gray-50 p-3 text-sm text-gray-500">
            No hay colores disponibles. Ve a Configuración → Gestión de Colores para activar o crear colores.
          </div>
        )
      )}

      {colorsOnly ? (
        <div className="flex flex-wrap gap-2">
          {colors.map((color) => <div key={color} className="flex items-center gap-1 rounded border p-1">
            <ColorVariantBadge color={color} variant={variants[color]} colorSettings={colorSettings} />
            {!readOnly && <Button type="button" size="icon" variant="ghost" aria-label={`Eliminar variante ${color}`} onClick={() => onRemoveColor?.(color)}><Trash2 className="h-4 w-4" /></Button>}
          </div>)}
          {!colors.length && <p className="text-sm text-gray-500">Sin variantes predefinidas. Puedes añadirlas opcionalmente.</p>}
        </div>
      ) : colors.length === 0 ? (
        <div className="rounded border border-dashed p-5 text-center text-sm text-gray-500">
          Añade una línea de color para introducir cantidades en las tallas P a 5XL.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px] border-collapse text-center text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-28 border px-1 py-2 text-left">Color</th>
                {!hideContrasts && <><th className="w-20 border px-1 py-2 text-left">Contr. 1</th>
                <th className="w-20 border px-1 py-2 text-left">Contr. 2</th>
                <th className="w-20 border px-1 py-2 text-left">Contr. 3</th></>}
                {!readOnly && <th className="w-24 border px-1 py-2">Rellenar</th>}
                {SIMULATION_SIZES.map((size) => <th key={size} className="w-14 border px-1 py-2" translate="no">{size}</th>)}
                <th className="w-20 border px-1 py-2">Total</th>
                {!readOnly && <th className="w-10 border px-1 py-2"></th>}
              </tr>
            </thead>
            <tbody>
              {colors.map((color) => {
                const rowTotal = SIMULATION_SIZES.reduce((sum, size) => sum + Number(quantities[cellKey(color, size)] || 0), 0);
                return (
                  <tr key={color}>
                    <td className="border px-1 py-2 text-left">
                      <ColorVariantBadge color={color} variant={variants[color]} colorSettings={colorSettings} />
                    </td>
                    {!hideContrasts && ["contrast_1", "contrast_2", "contrast_3"].map((field) => (
                      <td key={field} className="border px-1 py-2">
                        <Input value={contrasts[color]?.[field] || ""} disabled={readOnly} onChange={(event) => onContrastChange?.(color, field, event.target.value)} className="h-8 w-full px-1 text-xs" />
                      </td>
                    ))}
                    {!readOnly && <td className="border px-1 py-2"><RowQuickFill onApply={(value) => onFillRow?.(color, value)} /></td>}
                    {SIMULATION_SIZES.map((size) => {
                      const key = cellKey(color, size);
                      return (
                        <td key={key} className="border px-1 py-2">
                          <Input aria-label={`${colorSettings?.[color]?.description || color} · ${size}`} type="number" min="0" step="1" value={quantities[key] || ""} disabled={readOnly} onChange={(event) => onChange?.(key, event.target.value)} className="mx-auto h-8 w-12 px-1 text-center" />
                        </td>
                      );
                    })}
                    <td className="border px-1 py-2 font-semibold">{rowTotal}</td>
                    {!readOnly && <td className="border px-1 py-2"><Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => onRemoveColor?.(color)}><Trash2 className="h-4 w-4" /></Button></td>}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50 font-semibold">
                <td className="border px-1 py-2 text-left">Total prendas</td>
                {(!hideContrasts || !readOnly) && <td className="border px-1 py-2" colSpan={(hideContrasts ? 0 : 3) + (readOnly ? 0 : 1)}></td>}
                {SIMULATION_SIZES.map((size) => <td key={size} className="border px-1 py-2">{colors.reduce((sum, color) => sum + Number(quantities[cellKey(color, size)] || 0), 0)}</td>)}
                <td className="border px-1 py-2">{totalQuantity}</td>
                {!readOnly && <td className="border px-1 py-2"></td>}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
